import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { DeveloperProvider } from './contexts/DeveloperContext';
import { GlobalNotificationBar } from './components/GlobalNotificationBar';
import { HeroUIProvider } from '@heroui/react';
import './index.css';

// Lazy load components
const Auth = lazy(() => import('./pages/Auth'));
const AuthBuilder = lazy(() => import('./pages/AuthBuilder'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NewsFeed = lazy(() => import('./pages/NewsFeed'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupFeed = lazy(() => import('./pages/GroupFeed'));
const GroupSettings = lazy(() => import('./pages/GroupSettings'));
const Chat = lazy(() => import('./pages/Chat'));
const Search = lazy(() => import('./pages/Search'));
const Profile = lazy(() => import('./pages/Profile'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Settings = lazy(() => import('./pages/Settings'));
const Classroom = lazy(() => import('./pages/Classroom'));
const CourseCards = lazy(() => import('./pages/CourseCards'));
const CourseViewer = lazy(() => import('./pages/CourseViewer'));
const CourseBuilderStep1 = lazy(() => import('./pages/CourseBuilderStep1'));
const CourseBuilderStep2 = lazy(() => import('./pages/CourseBuilderStep2'));
const CourseBuilderStep3 = lazy(() => import('./pages/CourseBuilderStep3'));
const CourseBuilderStep4 = lazy(() => import('./pages/CourseBuilderStep4'));
const ModuleBuilderStep1 = lazy(() => import('./pages/ModuleBuilderStep1'));
const ModuleBuilderStep2 = lazy(() => import('./pages/ModuleBuilderStep2'));
const ModuleBuilderStep3 = lazy(() => import('./pages/ModuleBuilderStep3'));
const ModuleBuilderStep3b = lazy(() => import('./pages/ModuleBuilderStep3b'));
const ModuleBuilderStep4 = lazy(() => import('./pages/ModuleBuilderStep4'));
const CreateProfile = lazy(() => import('./pages/CreateProfile'));
const OnboardingGoals = lazy(() => import('./pages/OnboardingGoals'));
const MembershipSelection = lazy(() => import('./pages/MembershipSelection'));
const TopicFeed = lazy(() => import('./pages/TopicFeed'));
const QRCodePage = lazy(() => import('./pages/QRCodePage'));
const DeveloperPage = lazy(() => import('./pages/DeveloperPage'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const GamificationDashboard = lazy(() => import('./components/GamificationDashboard'));
const Achievements = lazy(() => import('./pages/Achievements'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

// Auth router component
const AuthRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return user ? <>{children}</> : <Navigate to="/auth" replace />;
};

function App() {
  return (
    <HeroUIProvider>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <DeveloperProvider>
              <Router>
                <GlobalNotificationBar />
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/builder" element={<AuthBuilder />} />
                    <Route path="/developer" element={<DeveloperPage />} />
                    <Route
                      path="/admin"
                      element={
                        <AuthRouter>
                          <AdminPanel />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/onboarding/goals"
                      element={
                        <AuthRouter>
                          <OnboardingGoals />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/onboarding/profile"
                      element={
                        <AuthRouter>
                          <CreateProfile />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/membership"
                      element={
                        <AuthRouter>
                          <MembershipSelection />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        <AuthRouter>
                          <Dashboard />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <AuthRouter>
                          <Dashboard />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/newsfeed"
                      element={
                        <AuthRouter>
                          <NewsFeed />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/groups"
                      element={
                        <AuthRouter>
                          <Groups />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/groups/:groupId"
                      element={
                        <AuthRouter>
                          <GroupFeed />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/groups/:groupId/settings"
                      element={
                        <AuthRouter>
                          <GroupSettings />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/chat"
                      element={
                        <AuthRouter>
                          <Chat />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/search"
                      element={
                        <AuthRouter>
                          <Search />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <AuthRouter>
                          <Profile />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <AuthRouter>
                          <Settings />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/classroom"
                      element={
                        <AuthRouter>
                          <Classroom />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/courses"
                      element={
                        <AuthRouter>
                          <CourseCards />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/courses/:courseId"
                      element={
                        <AuthRouter>
                          <CourseViewer />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/courses/:courseId/builder/step1"
                      element={
                        <AuthRouter>
                          <CourseBuilderStep1 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/courses/:courseId/builder/step2"
                      element={
                        <AuthRouter>
                          <CourseBuilderStep2 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/courses/:courseId/builder/step3"
                      element={
                        <AuthRouter>
                          <CourseBuilderStep3 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/courses/:courseId/builder/step4"
                      element={
                        <AuthRouter>
                          <CourseBuilderStep4 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/modules/:moduleId/builder/step1"
                      element={
                        <AuthRouter>
                          <ModuleBuilderStep1 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/modules/:moduleId/builder/step2"
                      element={
                        <AuthRouter>
                          <ModuleBuilderStep2 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/modules/:moduleId/builder/step3"
                      element={
                        <AuthRouter>
                          <ModuleBuilderStep3 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/modules/:moduleId/builder/step3b"
                      element={
                        <AuthRouter>
                          <ModuleBuilderStep3b />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/modules/:moduleId/builder/step4"
                      element={
                        <AuthRouter>
                          <ModuleBuilderStep4 />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/topics/:topicId"
                      element={
                        <AuthRouter>
                          <TopicFeed />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/qrcode"
                      element={
                        <AuthRouter>
                          <QRCodePage />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/user/:username"
                      element={
                        <AuthRouter>
                          <UserProfile />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/gamification"
                      element={
                        <AuthRouter>
                          <GamificationDashboard />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/achievements"
                      element={
                        <AuthRouter>
                          <Achievements />
                        </AuthRouter>
                      }
                    />
                    <Route
                      path="/feedback"
                      element={
                        <AuthRouter>
                          <FeedbackPage />
                        </AuthRouter>
                      }
                    />
                  </Routes>
                </Suspense>
              </Router>
            </DeveloperProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </HeroUIProvider>
  );
}

export default App;
